import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, InputAdornment, Popover, TextField, Typography, useMediaQuery } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CloseIcon from '@mui/icons-material/Close';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { APP_DATE_FORMAT, ISO_DATE_FORMAT } from '../utils/formatters';

const MONTH_SWIPE_THRESHOLD_PX = 48;

type DateInputFieldProps = {
  label: React.ReactNode;
  value?: string | null;
  onChange: (value: string) => void;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  sx?: SxProps<Theme>;
  minDate?: string | Date | Dayjs | null;
  maxDate?: string | Date | Dayjs | null;
  autoComplete?: string;
  inputRef?: React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null> | ((instance: HTMLInputElement | null) => void);
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLElement>;
};

const normalizeDate = (value?: string | Date | Dayjs | null): Dayjs | null => {
  if (!value) {
    return null;
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const formatForDisplay = (value?: string | null): string => {
  const parsed = normalizeDate(value);
  return parsed ? parsed.format(APP_DATE_FORMAT) : '';
};

// APP_DATE_FORMAT is 'DD.MM.YYYY' -> segment lengths [2, 2, 4]. Derived from the constant
// (rather than hardcoded) so this keeps working if the app date format ever changes.
const DATE_FORMAT_SEGMENT_LENGTHS = APP_DATE_FORMAT.split('.').map((segment) => segment.length);
const DATE_FORMAT_MAX_DIGITS = DATE_FORMAT_SEGMENT_LENGTHS.reduce((sum, len) => sum + len, 0);

// Auto-inserts the '.' separators as the user types digits, so typing "03052025" becomes
// "03.05.2025" without them having to type the dots themselves. The dot is appended the
// moment a segment (day, then month) is complete - not only once the next segment's first
// digit arrives - so it shows up immediately rather than one keystroke late. Any non-digit
// characters (including dots the user does type, or a pasted "03/05/2025") are stripped and
// the digits are re-grouped into the right segments, so it's harmless if they type separators.
const maskDateDigitsFromDigits = (digits: string): string => {
  let result = '';
  let consumed = 0;
  DATE_FORMAT_SEGMENT_LENGTHS.forEach((length, index) => {
    const segmentDigits = digits.slice(consumed, consumed + length);
    result += segmentDigits;
    consumed += segmentDigits.length;
    const isLastSegment = index === DATE_FORMAT_SEGMENT_LENGTHS.length - 1;
    if (!isLastSegment && segmentDigits.length === length) {
      result += '.';
    }
  });
  return result;
};

const maskDateDigits = (raw: string): string =>
  maskDateDigitsFromDigits(raw.replace(/\D/g, '').slice(0, DATE_FORMAT_MAX_DIGITS));

// The part of the template the user hasn't typed into yet, e.g. text "25." (day done) ->
// "MM.YYYY". Because maskDateDigits always keeps the typed text aligned character-for-character
// with APP_DATE_FORMAT (digits standing in for D/M/Y, dots landing exactly where the template's
// dots are), the remaining template is simply everything after what's been typed so far.
const remainingDateTemplate = (text: string): string =>
  text.length < APP_DATE_FORMAT.length ? APP_DATE_FORMAT.slice(text.length) : '';

// A free-typing date field: the user can click anywhere in the text and edit just the
// characters they want, like any ordinary text input. A calendar icon opens a picker
// as an alternative way to set the date.
export const DateInputField: React.FC<DateInputFieldProps> = ({
  label,
  value,
  onChange,
  fullWidth = false,
  size = 'medium',
  required = false,
  disabled = false,
  error = false,
  helperText,
  sx,
  minDate,
  maxDate,
  autoComplete,
  inputRef: forwardedInputRef,
  inputProps,
  onKeyDown,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('sm'));
  const [text, setText] = useState(() => formatForDisplay(value));
  const [focused, setFocused] = useState(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [mobileCalendarMonth, setMobileCalendarMonth] = useState<Dayjs | null>(null);
  const mobileCalendarTouchStartRef = useRef<{ x: number; y: number } | null>(null);
  const mobileCalendarTouchCurrentRef = useRef<{ x: number; y: number } | null>(null);

  // Geometry of the real <input>, measured rather than guessed, so the grey "remaining
  // template" overlay below lines up exactly with where the typed text sits - regardless of
  // size ("small"/"medium"), theme padding, or whether a helper/error text is showing beneath.
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const inputElRef = useRef<HTMLInputElement | null>(null);
  const assignInputRef = useCallback((node: HTMLInputElement | null) => {
    inputElRef.current = node;
    if (!forwardedInputRef) {
      return;
    }
    if (typeof forwardedInputRef === 'function') {
      forwardedInputRef(node);
      return;
    }
    forwardedInputRef.current = node;
  }, [forwardedInputRef]);
  const [inputMetrics, setInputMetrics] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
    paddingLeft: string;
    paddingRight: string;
    fontFamily: string;
    fontSize: string;
    fontWeight: string;
    lineHeight: string;
    letterSpacing: string;
  } | null>(null);

  useLayoutEffect(() => {
    const measure = () => {
      const inputEl = inputElRef.current;
      const wrapperEl = wrapperRef.current;
      if (!inputEl || !wrapperEl) return;
      const inputRect = inputEl.getBoundingClientRect();
      const wrapperRect = wrapperEl.getBoundingClientRect();
      const computed = window.getComputedStyle(inputEl);
      setInputMetrics({
        left: inputRect.left - wrapperRect.left,
        top: inputRect.top - wrapperRect.top,
        width: inputRect.width,
        height: inputRect.height,
        paddingLeft: computed.paddingLeft,
        paddingRight: computed.paddingRight,
        fontFamily: computed.fontFamily,
        fontSize: computed.fontSize,
        fontWeight: computed.fontWeight,
        lineHeight: computed.lineHeight,
        letterSpacing: computed.letterSpacing,
      });
    };

    measure();

    const inputEl = inputElRef.current;
    if (!inputEl || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(inputEl);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [size, fullWidth, label]);

  useEffect(() => {
    if (!focused) {
      setText(formatForDisplay(value));
    }
  }, [value, focused]);

  const normalizedValue = normalizeDate(value);
  const normalizedMinDate = normalizeDate(minDate);
  const normalizedMaxDate = normalizeDate(maxDate);

  useEffect(() => {
    if (calendarOpen && isMobileView) {
      setMobileCalendarMonth((normalizedValue || normalizedMinDate || dayjs()).startOf('month'));
    }
  }, [calendarOpen, isMobileView, normalizedMinDate, normalizedValue]);

  const trimmed = text.trim();
  const parsedTyped = trimmed ? dayjs(trimmed, APP_DATE_FORMAT, true) : null;
  const isFormatInvalid = trimmed.length > 0 && !(parsedTyped && parsedTyped.isValid());

  const commitText = () => {
    if (!trimmed) {
      if (value) {
        onChange('');
      }
      return;
    }
    if (parsedTyped && parsedTyped.isValid()) {
      const iso = parsedTyped.format(ISO_DATE_FORMAT);
      if (iso !== normalizeDate(value)?.format(ISO_DATE_FORMAT)) {
        onChange(iso);
      } else {
        setText(formatForDisplay(value));
      }
    }
  };

  const remainingTemplate = focused ? remainingDateTemplate(text) : '';
  const showGhost = Boolean(remainingTemplate && inputMetrics);
  const visibleMobileCalendarValue =
    mobileCalendarMonth && normalizedValue && !normalizedValue.isSame(mobileCalendarMonth, 'month')
      ? null
      : normalizedValue;

  const shiftMobileCalendarMonth = (direction: 'previous' | 'next') => {
    setMobileCalendarMonth((currentMonth) => {
      const baseMonth = (currentMonth || normalizedValue || normalizedMinDate || dayjs()).startOf('month');
      let nextMonth = baseMonth.add(direction === 'next' ? 1 : -1, 'month').startOf('month');

      if (normalizedMinDate && nextMonth.isBefore(normalizedMinDate.startOf('month'), 'month')) {
        nextMonth = normalizedMinDate.startOf('month');
      }

      if (normalizedMaxDate && nextMonth.isAfter(normalizedMaxDate.startOf('month'), 'month')) {
        nextMonth = normalizedMaxDate.startOf('month');
      }

      return nextMonth;
    });
  };

  const handleMobileCalendarTouchStart = (event: React.TouchEvent<HTMLElement>) => {
    const touch = event.touches[0];
    mobileCalendarTouchStartRef.current = { x: touch.clientX, y: touch.clientY };
    mobileCalendarTouchCurrentRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleMobileCalendarTouchMove = (event: React.TouchEvent<HTMLElement>) => {
    if (!mobileCalendarTouchStartRef.current) {
      return;
    }
    const touch = event.touches[0];
    mobileCalendarTouchCurrentRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleMobileCalendarTouchEnd = () => {
    const start = mobileCalendarTouchStartRef.current;
    const current = mobileCalendarTouchCurrentRef.current;
    mobileCalendarTouchStartRef.current = null;
    mobileCalendarTouchCurrentRef.current = null;

    if (!start || !current) {
      return;
    }

    const deltaX = current.x - start.x;
    const deltaY = current.y - start.y;

    if (Math.abs(deltaX) < MONTH_SWIPE_THRESHOLD_PX || Math.abs(deltaX) <= Math.abs(deltaY)) {
      return;
    }

    shiftMobileCalendarMonth(deltaX < 0 ? 'next' : 'previous');
  };

  return (
    <>
      <Box ref={wrapperRef} sx={{ position: 'relative' }}>
        {showGhost && inputMetrics && (
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              left: inputMetrics.left,
              top: inputMetrics.top,
              width: inputMetrics.width,
              height: inputMetrics.height,
              // Sits visually in front of the real input rather than behind it: the invisible
              // spacer span below just reserves the width of what's already been typed (so the
              // grey text starts in the right place), and paints nothing there, letting the
              // real input's own text show through untouched.
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              pointerEvents: 'none',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              paddingLeft: inputMetrics.paddingLeft,
              paddingRight: inputMetrics.paddingRight,
              fontFamily: inputMetrics.fontFamily,
              fontSize: inputMetrics.fontSize,
              fontWeight: inputMetrics.fontWeight,
              lineHeight: inputMetrics.lineHeight,
              letterSpacing: inputMetrics.letterSpacing,
            }}
          >
            <span style={{ visibility: 'hidden' }}>{text}</span>
            <span style={{ color: 'rgba(0, 0, 0, 0.38)' }}>{remainingTemplate}</span>
          </Box>
        )}
        <TextField
          ref={anchorRef}
          inputRef={assignInputRef}
          label={label}
          value={text}
          onChange={(event) => {
            const rawValue = event.target.value;
            const prevDigitCount = text.replace(/\D/g, '').length;
            let nextDigits = rawValue.replace(/\D/g, '').slice(0, DATE_FORMAT_MAX_DIGITS);
            // Backspacing "over" an auto-inserted dot only removes that separator character -
            // the digit count comes out unchanged - so continue the deletion into the digit
            // before it. Otherwise backspace right after a completed segment would look like
            // it does nothing, since the dot we removed gets put straight back on remask.
            if (rawValue.length < text.length && nextDigits.length === prevDigitCount) {
              nextDigits = nextDigits.slice(0, -1);
            }
            setText(maskDateDigitsFromDigits(nextDigits));
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            commitText();
          }}
          onKeyDown={(event) => {
            onKeyDown?.(event);
            if (!event.defaultPrevented && event.key === 'Enter') {
              event.currentTarget.blur();
            }
          }}
          placeholder={APP_DATE_FORMAT}
          autoComplete={autoComplete}
          inputProps={inputProps}
          fullWidth={fullWidth}
          size={size}
          required={required}
          disabled={disabled}
          error={error || isFormatInvalid}
          helperText={isFormatInvalid ? t('validation.invalidDateFormat', { format: APP_DATE_FORMAT, defaultValue: `Invalid date, use ${APP_DATE_FORMAT}` }) : helperText}
          sx={sx}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  disabled={disabled}
                  onClick={() => setCalendarOpen(true)}
                  edge="end"
                >
                  <CalendarMonthIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {isMobileView ? (
        <Dialog open={calendarOpen} onClose={() => setCalendarOpen(false)} fullScreen>
          <DialogTitle
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                  {label}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                  {t('validation.invalidDateFormat', {
                    format: APP_DATE_FORMAT,
                    defaultValue: `Use ${APP_DATE_FORMAT}`,
                  })}
                </Typography>
              </Box>
              <IconButton aria-label={t('common.close', 'Close')} onClick={() => setCalendarOpen(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent
            sx={{ px: 0, py: 2, display: 'flex', justifyContent: 'center', touchAction: 'pan-y' }}
            onTouchStart={handleMobileCalendarTouchStart}
            onTouchMove={handleMobileCalendarTouchMove}
            onTouchEnd={handleMobileCalendarTouchEnd}
          >
            <DateCalendar
              key={mobileCalendarMonth?.format('YYYY-MM') || 'mobile-date-calendar'}
              value={visibleMobileCalendarValue}
              referenceDate={mobileCalendarMonth ?? normalizedValue ?? normalizedMinDate ?? undefined}
              minDate={normalizedMinDate ?? undefined}
              maxDate={normalizedMaxDate ?? undefined}
              onMonthChange={(month) => setMobileCalendarMonth(month.startOf('month'))}
              onChange={(date) => {
                const iso = date?.isValid() ? date.format(ISO_DATE_FORMAT) : '';
                onChange(iso);
                setText(date?.isValid() ? date.format(APP_DATE_FORMAT) : '');
                setCalendarOpen(false);
              }}
            />
          </DialogContent>
        </Dialog>
      ) : (
        <Popover
          open={calendarOpen}
          anchorEl={anchorRef.current}
          onClose={() => setCalendarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <DateCalendar
            value={normalizeDate(value)}
            minDate={normalizeDate(minDate) ?? undefined}
            maxDate={normalizeDate(maxDate) ?? undefined}
            onChange={(date) => {
              const iso = date?.isValid() ? date.format(ISO_DATE_FORMAT) : '';
              onChange(iso);
              setText(date?.isValid() ? date.format(APP_DATE_FORMAT) : '');
              setCalendarOpen(false);
            }}
          />
        </Popover>
      )}
    </>
  );
};
